import { API_URL, fetchWithAuth } from "../utils/api";
import { UserData, getCurrentUser, normalizeUserData } from "./auth";
import { normalizeUserDataTextFields } from "../utils/textNormalization";

// API version constant - must match server expectations
const API_VERSION = "2";

export interface EditUserData {
  nickname: string;
  email: string;
  fullname?: string;
  password?: string;
  subscribed?: boolean;
  /** Must be accompanied by a verification token when supplied. */
  phone?: string;
  phoneVerificationToken?: string;
}

export interface SetAvatarResponse {
  avatar: string;
}

export interface DetailedUser extends UserData {
  subscribed?: boolean;
  vkUserId?: string;
}

export const editUser = async (userData: EditUserData): Promise<UserData> => {
  // Clean up fullname - trim and set to undefined if empty
  const cleanData = {
    ...userData,
    fullname:
      userData.fullname && userData.fullname.trim().length
        ? userData.fullname.trim()
        : undefined,
    api_version: API_VERSION,
  };

  const response = await fetchWithAuth(`${API_URL}/user/edit`, {
    method: "POST",
    body: JSON.stringify(cleanData),
  });

  // The edit endpoint returns a nested userData object without every local field.
  // Merge it over the current user so admin/social fields are not dropped.
  const updatedUser = normalizeUserData(response, getCurrentUser());

  localStorage.setItem("user", JSON.stringify(updatedUser));
  return updatedUser;
};

export const setAvatar = async (file: File): Promise<UserData> => {
  const formData = new FormData();
  formData.append("avatar", file);

  const token = localStorage.getItem("authToken");
  if (!token) {
    throw new Error("Not authenticated");
  }

  const response = await fetch(`${API_URL}/user/setAvatar`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to upload avatar");
  }

  const data = await response.json();

  // The avatar endpoint returns only { avatar }; keep the rest of the user.
  const currentUser = getCurrentUser();
  const updatedUser = currentUser
    ? normalizeUserData({ ...currentUser, avatar: data.avatar }, currentUser)
    : normalizeUserData(data);

  localStorage.setItem("user", JSON.stringify(updatedUser));
  return updatedUser;
};

export const getUserDetails = async (
  nickname: string,
): Promise<DetailedUser> => {
  const data = await fetchWithAuth(
    `${API_URL}/user/details/${encodeURIComponent(nickname)}`,
    {
      method: "GET",
    },
  );
  return normalizeUserDataTextFields(data);
};

export const deleteSelf = async (): Promise<void> => {
  await fetchWithAuth(`${API_URL}/user/delete`, {
    method: "DELETE",
  });

  // Clear all auth data
  localStorage.removeItem("authToken");
  localStorage.removeItem("user");
};
